import constants from '@/constant'
import socketHelper from '@/helpers/socket/socket-helper'
import utils from '@/util'
import OpenAIApi from '@/api/openai'
import store from '@/vuex/store'
import i18nPresenter from '@/i18nPresenter'
import SlideApi from '@/api/slide'
import { SimpleCache } from '@/utils/cache.utils'

const cache = new SimpleCache()

const updateSlideActiveAction = ({ commit }, payload) => {
  commit('updateSlideActive', payload)
}

const updateSlideByIdAction = ({ commit }, payload) => {
  commit('updateSlideById', payload)
}

const setSlideActiveAction = ({ commit, state }, payload) => {
  payload = payload || {}
  payload.randomIndexId = utils.generateRandomString() // https://ahaslides.atlassian.net/browse/AHA-23989
  payload.SlideOptions = payload.SlideOptions || []

  const slideTypes = [
    'bulletPoints',
    'multipleChoice',
    'pickAnswer',
    'imageChoice',
    'scale',
  ]

  const customDefaultSlideOptionsType = ['matchPairs', 'correctOrder']

  // Create default SlideOptions
  if (slideTypes.includes(payload.type) &&
    !customDefaultSlideOptionsType.includes(payload.slideType) &&
    payload.SlideOptions.length === 0) {
    payload.SlideOptions = [
      { index: 1, order: 1, randomIndexId: utils.generateRandomString() },
      { index: 2, order: 2, randomIndexId: utils.generateRandomString() },
      { index: 3, order: 3, randomIndexId: utils.generateRandomString() },
    ]
  }

  if (payload.type === 'voiceQA' && payload.SlideOptions.length === 0) {
    payload.SlideOptions = [
      {
        index: 1, order: 1, title: 'Your name', randomIndexId: utils.generateRandomString(),
      },
      {
        index: 2, order: 2, title: 'Your sitting area', randomIndexId: utils.generateRandomString(),
      },
    ]
  }

  if (payload.type === 'ideas') {
    payload.limitChoice = Math.min(constants.MAX_VOTE_IDEAS, payload.limitChoice)
    payload.limitChoice = Math.max(constants.MIN_VOTE_IDEAS, payload.limitChoice)
  } else {
    payload.limitChoice = Math.max(2, payload.limitChoice || 2)
  }

  if (payload.type === 'bulletPoints') {
    payload.bulletPoints = payload.bulletPoints || []
  }

  if (state.route.name === 'PresentationDetail' &&
    state.lastSlideActiveId &&
    payload.id &&
    state.lastSlideActiveId !== payload.id
  ) {
    socketHelper.leaveRoomSlide(state.presentation.uniqueAccessCode, state.lastSlideActiveId)
    socketHelper.joinRoomSlide(state.presentation.uniqueAccessCode, payload.id)
  }

  commit('setSlideActive', payload)
}

const checkSlideMovingDirectionAction = ({ commit }, slideActiveId) => {
  commit('checkSlideMovingDirection', slideActiveId)
}

const concatSlideOptionsAction = ({ commit, state }, payload) => {
  const currentSlideOptions = state.slideActive.SlideOptions || []
  const newSlideOptions = payload.slideOptions
  commit('updateSlideById', {
    id: payload.slideId,
    SlideOptions: currentSlideOptions.concat(newSlideOptions),
  })
}

const generateOptionsAction = async ({ commit }, {
  slideId,
  onGenerateOptionsSuccess,
  onGenerateOptionsError,
}) => {
  try {
    const { slide: slideObject } = await OpenAIApi.generateOptions({ slideId })

    if (!slideObject) {
      store.dispatch('setSyncLoadingAction', 3)
      return
    }

    slideObject.otherCorrectQuiz = slideObject.otherCorrectQuizJson || slideObject.otherCorrectQuiz

    await commit('updateSlideById', {
      id: slideId,
      ...slideObject,
    })
    onGenerateOptionsSuccess(slideObject.SlideOptions)
    utils.showToastSuccess('Options have been generated successfully!')
  } catch (error) {
    utils.showToastError(i18nPresenter.t('Something went wrong. Please try again.'))
    onGenerateOptionsError(error)
  }
}

const updateSlideCanvasBlockByIdAction = async ({ commit }, payload) => {
  if (!payload.canvasBlocksUrl) return null

  const canvasBlocks = await fetch(payload.canvasBlocksUrl).then((res) => res.json()).then((body) => ({ body }))
  if (!canvasBlocks || !canvasBlocks.body) return null
  payload.canvasBlocks = canvasBlocks.body
  commit('updateSlideById', payload)
  return null
}

const getSlideAttributesAction = async ({ rootGetters }, slideId) => {
  const canUseSlidePlugin = rootGetters['slidePlugin/canUseSlidePlugin']
  const attributeList = await SlideApi.getAttributesBySlideId({
    slideId,
    ...(canUseSlidePlugin ? { resignImage: true } : {}),
  })
  return (attributeList ?? []).reduce((obj, attr) => {
    obj[attr.type] = attr.attributes
    return obj
  }, {})
}

const getSlideAttributesWithCacheAction = async ({ dispatch }, slideId) => {
  return cache.invoke(() => {
    return dispatch('getSlideAttributesAction', slideId)
  }, {
    key: `getSlideAttributesAction-${slideId}`,
    maxAgeInSeconds: 5,
  })
}

const upsertSlideAttributeAction = async (_, { slideId, attributeKey, attributeValue }) => {
  return SlideApi.upsertSlideAttribute(slideId, {
    attributeKey,
    attributeValue,
  })
}

const getSlideAttributesByPresentationId = async (_, { presentationId, attributeKeys }) => {
  return SlideApi.getSlideAttributesByPresentationId({ presentationId, attributeKeys })
}

export default {
  // use action which has suffix 'Action'
  updateSlideActiveAction,
  updateSlideActive: updateSlideActiveAction,
  updateSlideByIdAction,
  setSlideActiveAction,
  setSlideActive: setSlideActiveAction,
  checkSlideMovingDirectionAction,
  concatSlideOptionsAction,
  generateOptionsAction,
  updateSlideCanvasBlockByIdAction,
  getSlideAttributesAction,
  upsertSlideAttributeAction,
  getSlideAttributesWithCacheAction,
  getSlideAttributesByPresentationId,

  // action hook for slide events
  'SLIDE/NAVIGATION': async () => { },
}
